import { AxiosError, AxiosResponse } from 'axios';
import { Moment } from 'moment-timezone';
import { UseMutationOptions } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import { JobPostingDetail } from '../types';
import useUpdateVacancyMutation from './useUpdateVacancyMutation';

interface PublicationMutationVariables {
  externalPublication: boolean;
  externalPublicationPeriod?: [Moment, Moment] | null;
  timezone: string;
  description: string;
  competencies?: number[];
  workModel?: JobPostingDetail['work_model'];
  hireModel?: JobPostingDetail['hire_model'];
  country: string | null;
  state: string | null;
  city: string | null;
  jobPostingId?: JobPostingDetail['id'];
  googleForJobsExternalPublication: boolean;
  netvagasExternalPublication: boolean;
  linkedinExternalPublication: boolean;
  careerjetExternalPublication: boolean;
  joobleExternalPublication: boolean;
  unlistedExternalPublication: boolean;
  whatsAppSubscription: boolean;
}

const usePublicationMutation = (
  options?: Omit<
    UseMutationOptions<
      AxiosResponse<JobPostingDetail, unknown>,
      AxiosError<unknown, unknown>,
      PublicationMutationVariables,
      unknown
    >,
    'mutationFn'
  >
) => {
  const updateVacancyMutation = useUpdateVacancyMutation();

  return useAPIMutation(
    async (
      {
        timezone,
        externalPublication,
        externalPublicationPeriod,
        country,
        state,
        city,
        jobPostingId,
        googleForJobsExternalPublication,
        netvagasExternalPublication,
        linkedinExternalPublication,
        unlistedExternalPublication,
        careerjetExternalPublication,
        joobleExternalPublication,
        whatsAppSubscription,
      }: PublicationMutationVariables,
      api
    ) => {
      const publishedJobPosting = await updateVacancyMutation.mutateAsync({
        jobPostingId,
        jobPostingFields: {
          timezone,
          country: country?.trim() || null,
          state: state?.trim() || null,
          city: city?.trim() || null,
          external_publication: externalPublication,
          external_publication_start_at:
            externalPublicationPeriod && externalPublicationPeriod.length === 2
              ? `${externalPublicationPeriod[0].format('YYYY-MM-DD')}T00:00:00Z`
              : Array.isArray(externalPublicationPeriod)
              ? undefined
              : externalPublicationPeriod,
          external_publication_end_at:
            externalPublicationPeriod && externalPublicationPeriod.length === 2
              ? `${externalPublicationPeriod[1].format('YYYY-MM-DD')}T23:59:59Z`
              : Array.isArray(externalPublicationPeriod)
              ? undefined
              : externalPublicationPeriod,
          unlisted_external_publication: unlistedExternalPublication,
          netvagas_external_publication: netvagasExternalPublication,
          google_for_jobs_external_publication: googleForJobsExternalPublication,
          linkedin_external_publication: linkedinExternalPublication || false,
          careerjet_external_publication: careerjetExternalPublication || false,
          jooble_external_publication: joobleExternalPublication || false,
          whatsapp_subscription: whatsAppSubscription,
        },
      });

      return publishedJobPosting;
    },
    options
  );
};

export default usePublicationMutation;
